import { useEffect, useRef, useState } from "react"
import { useQueue } from "@/context/QueueContext";
import { useNavigate } from "react-router-dom";
import ErrorState from "@/components/common/ErrorStateComponent";
import LoadingComponent from "@/components/common/LoadingComponent";
import { apiCall } from "@/api/apiCall";
import { processParams } from "../../utils/paramsHelper";
import Mawgif_Group_Table_Component, { PAGE_BREAK_AFTER_LABEL } from "../../components/consolidate_boardpack_components/Mawgif_Group_Table_Component"
import HeadingComponent from "../../components/shared/heading";
import { dynamicKey } from "../../utils/dynamicKey";

const mawgifRequestCache = new Map();

// BoardPackLayout (non-profile pages) ka content area = 720 - TOP_SPACE(56) - BOTTOM_SPACE(110) = 554px.
// Footer ke logos + page number asal me neeche sirf ~48px lete hain, baqi BOTTOM_SPACE me
// khali jagah bachti hai. Ye utni extra height (px) hai jo hum is page ke liye neeche se wapas le rahe hain.
// Page number / logos se takraye to ghatao (e.g. 40), jagah bache to barhao (e.g. 52).
const BOTTOM_RECLAIM = 48;

const Mawgif_Group_Table = ({ year = "", month = "", contract = "", page = null, isRefresh = false }) => {
  const { enqueue } = useQueue();
  const navigate = useNavigate();
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [tableData, setTableData] = useState(null);
  const hasFetchedRef = useRef(false);

  const configuredPart = page?.part === 2 ? 2 : page?.part === 1 ? 1 : null;

  const fetchData = async () => {
    try {
      setLoading(true);
      setError(null);

      const apiParams = processParams(page?.params || {}, { year, month, contract });
      const cacheKey = JSON.stringify(apiParams);

      let cached = mawgifRequestCache.get(cacheKey);
      if (!cached) {
        const promise = enqueue(() =>
          apiCall("GET", "/boardpack/consolidate", apiParams, navigate, false, false, isRefresh)
        );
        cached = { promise };
        mawgifRequestCache.set(cacheKey, cached);
      }

      const response = cached.data
        ? { success: true, data: cached.data }
        : await cached.promise;

      if (!response?.success) {
        mawgifRequestCache.delete(cacheKey);
        throw new Error(response?.message || "API returned unsuccessful response");
      }

      mawgifRequestCache.set(cacheKey, { data: response.data });
      setTableData(response.data);
    } catch (err) {
      console.error("Mawgif Group Table fetch error:", err);
      setError(err.message || "Failed to load data. Please try again later.");
    } finally {
      setLoading(false);
    }
  };

  // On refresh, BoardPack remounts this component with a fresh key, so
  // the old cached response for these exact params is stale — drop it.
  // NOTE: this effect must stay ABOVE the fetch effect, so the stale entry
  // is removed before fetchData() looks at the cache.
  useEffect(() => {
    if (!isRefresh) return;
    const apiParams = processParams(page?.params || {}, { year, month, contract });
    mawgifRequestCache.delete(JSON.stringify(apiParams));
  }, []);

  useEffect(() => {
    if (!year || !month || !contract) return;
    if (hasFetchedRef.current) return;

    hasFetchedRef.current = true;
    fetchData();
  }, [year, month, contract]);

  if (loading) {
    return <LoadingComponent />;
  }

  if (error) {
    return <ErrorState error={error} onRetry={fetchData} />;
  }

  const allRows = tableData?.rows || [];
  const breakIdx = allRows.findIndex((r) => r.label === PAGE_BREAK_AFTER_LABEL);
  const page1Rows = breakIdx === -1 ? allRows : allRows.slice(0, breakIdx + 1);
  const page2Rows = breakIdx === -1 ? [] : allRows.slice(breakIdx + 1);

  let rowsForThisPart;
  if (configuredPart === 1) rowsForThisPart = page1Rows;
  else if (configuredPart === 2) rowsForThisPart = page2Rows;
  else rowsForThisPart = allRows;

  // Dono parts me se jis me zyada rows hain uski ginti: component isi se font scale
  // nikalta hai, taake Part 1 aur Part 2 ka header size same rahe.
  const maxRows = configuredPart ? Math.max(page1Rows.length, page2Rows.length) : allRows.length;

  // BoardPackLayout children ko vertical center karta hai (items-center). alignSelf flex-start se
  // root ko top par pin karte hain, phir height me BOTTOM_RECLAIM add karte hain, taake content
  // sirf neeche reserved khali jagah tak barhe (upar nahi).
  return (
    <div
      className='flex flex-col gap-2 w-full min-h-0'
      style={{ alignSelf: 'flex-start', height: `calc(100% + ${BOTTOM_RECLAIM}px)` }}
    >
      <div className="flex flex-col gap-0 shrink-0 [&_h1]:my-0 [&_h6]:my-0">
        {page?.title && <HeadingComponent variant="main">{dynamicKey({ text: page?.title, year, month, contract })}</HeadingComponent>}
        {page?.subtitle && <HeadingComponent variant="sub">{dynamicKey({ text: page?.subtitle, year, month, contract })}</HeadingComponent>}
      </div>

      {/* Heading ke baad root ki saari bachi hui height: table aur comment box dono isi ko follow karte hain */}
      <div className='flex flex-row gap-1 w-full flex-1 min-h-0 items-stretch px-2'>

        <div className='flex-1 min-w-0 h-full'>
          <Mawgif_Group_Table_Component
            page={page}
            rows={rowsForThisPart}
            maxRows={maxRows}
            headerYears={tableData?.headerYears}
            headerMonths={tableData?.headerMonths}
            headerMeasures={tableData?.headerMeasures}
          />
        </div>

        <div className='w-72 shrink-0 h-full'>
          <div className='!border p-4 h-full flex flex-col'>
            <h3 className='text-lg font-semibold mb-2'>Mawgif group Comments:</h3>
            <textarea
              className="w-full flex-1 p-2 border-0 focus:outline-none focus:ring-0 resize-none"
              style={{ textAlign: 'left' }}
              rows={6}
            />
          </div>
        </div>
      </div>
    </div>
  )
}

export default Mawgif_Group_Table